import { useAccommodationAvailability } from '@/domain/prices/availability/hooks'
import { usePricesStore } from '@/infrastructure/prices/usePricesStore'
import { useAccommodationContext } from '@/react-pages/Prices/AccommodationContext'
import { useDependencies } from '@/scopes/common/infrastructure/dependencies'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import * as React from 'react'
import { useCallback, useEffect } from 'react'

export function usePromotedPriceQueryKey() {
  const { filterSelection } = usePricesStore()
  const selection = (({ DepartureDate: _, ...object }) => object)(filterSelection)
  return React.useMemo(() => ['promoted-price', JSON.stringify(selection)], [selection])
}

export function usePromotedPrice() {
  const { promotedPriceAPIClient } = useDependencies()
  const { isAccommodationAvailable, transportTypes } = useAccommodationAvailability()
  const { filterSelection } = usePricesStore()
  const { contextItemId, accoId, promotedPriceId } = useAccommodationContext()

  const transportType = React.useMemo(
    () => filterSelection?.TransportType || transportTypes[0],
    [filterSelection?.TransportType, transportTypes],
  )

  const newFilterSelection = React.useMemo(
    () => ({
      ...filterSelection,
      ...(transportType && { TransportType: transportType }),
    }),
    [filterSelection, transportType],
  )

  const queryKey = usePromotedPriceQueryKey()

  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey,
    queryFn: async () =>
      promotedPriceAPIClient.getPromotedPrice({
        contextItemId,
        accoId,
        promotedPriceId,
        filterSelection: newFilterSelection,
      }),
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    retry: false,
    enabled: isAccommodationAvailable,
  })

  return {
    data,
    isLoading,
    isError,
    isFetching,
    participants: filterSelection?.Participants,
  }
}

export function useDateCountdown(endDate: string) {
  const [timeToEnd, setTimeToEnd] = React.useState<string>('')
  const [counterEnded, setCounterEnded] = React.useState<boolean>(false)

  const pad = (value: number) => String(value).padStart(2, '0')

  const countTimeToEnd = useCallback((interval: NodeJS.Timeout) => {
    const deadline = new Date(endDate)
    const today = new Date()
    const timeDifference = deadline.getTime() - today.getTime()
    if (timeDifference <= 0) {
      setCounterEnded(true)
      clearInterval(interval)
      return
    }
    const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24))
    const hours = Math.floor((timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((timeDifference % (1000 * 60)) / 1000)
    setTimeToEnd(`${pad(days)} : ${pad(hours)} : ${pad(minutes)} : ${pad(seconds)}`)
  }, [endDate])

  useEffect(() => {
    const interval: NodeJS.Timeout = setInterval(() => countTimeToEnd(interval), 1000)
    return () => {
      clearInterval(interval)
    }
  }, [])
  return {
    counterEnded,
    timeToEnd,
  }
}
